/** @jsx isolateComponent */
import { useRef, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Animation,
  Colors,
  Icon,
  Link,
  Surface,
  Typography,
} from '@zapier/design-system';

import HelpCenterLinkIcon from '../../HelpCenterLinkIcon';
import { SizeType } from '../../../../types';
import { ServiceIcon } from '../../ServiceIcon';

type DocLinkProps = {
  /** Link */
  href: string;
  /** Title */
  title: string;
  /** App ID to render app icon instead of default icon */
  appId?: string;
  /** Size to display. */
  size?: SizeType;
};

const containerCss = (size: SizeType) => css`
  display: grid;
  grid-column-gap: ${size === 'small' ? '10px' : '20px'};
  grid-template-columns: min-content 1fr min-content;
  align-items: center;
  padding: 0 ${size === 'small' || '10px'};
  height: 50px;
`;

const titleCss = (isActive: boolean) => css`
  ${Typography.SmallPrint3}
  color: ${isActive ? Colors.UiPrimaryStrongest : undefined};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-decoration: none;
`;

const rightIconCss = (isActive: boolean) => css`
  transition-property: opacity;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  opacity: ${isActive ? 1 : 0};
  padding-top: 10px;
  padding-right: 10px;
`;

const helpLinkCss = css`
  text-decoration: none;
`;

/** Renders a single doc for `<DocLinks>`. */
const DocLink = ({ appId, href, size = 'large', title }: DocLinkProps) => {
  const [isActive, setIsActive] = useState(false);

  const onBlur = useRef(() => setIsActive(false)).current;
  const onFocus = useRef(() => setIsActive(true)).current;
  const onMouseEnter = useRef(() => setIsActive(true)).current;
  const onMouseLeave = useRef(() => setIsActive(false)).current;

  return (
    <Link
      css={helpLinkCss}
      href={href}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      target="_blank"
    >
      <Surface
        backgroundColor="PrimeWhite"
        borderColor="GrayWarm4"
        shadow={isActive ? 'elevation10' : undefined}
      >
        <div css={containerCss(size)}>
          {!appId && (
            <HelpCenterLinkIcon
              isActive={isActive}
              name="fileFilledFill"
              size={30}
            />
          )}
          {appId && <ServiceIcon alt={title} serviceName={appId} size={30} />}
          <span css={titleCss(isActive)}>{title}</span>
          <span css={rightIconCss(isActive)}>
            <Icon color="UiPrimaryStrongest" name="arrowOffsite" size={18} />
          </span>
        </div>
      </Surface>
    </Link>
  );
};

export default DocLink;
